if (!customElements.get('supplement-facts-drawer')) {
  customElements.define(
    'supplement-facts-drawer',
    class SupplementFactsDrawer extends HTMLElement {
      constructor() {
        super();
        this.drawerId = this.dataset.drawerId;
        this.liveRegionTemplate = this.dataset.liveRegionTemplate || '';
        this.dialog = this.querySelector('dialog');
        this.title = this.querySelector('.supplement-facts-dialog__title');
        this.openedBy = null;
        this.inertedElements = [];
        this.liveAnnouncementTimeout = null;
        this.previousBodyStyle = {};
        this.scrollY = 0;
        this.typeahead = '';
        this.typeaheadTimeout = null;
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleDialogKeydown = this.handleDialogKeydown.bind(this);
        this.handleClose = this.handleClose.bind(this);
        this.handleCloseButtonClick = this.handleCloseButtonClick.bind(this);
        this.handleOpenerClick = this.handleOpenerClick.bind(this);
        this.handleOptionClick = this.handleOptionClick.bind(this);
        this.handleOptionKeydown = this.handleOptionKeydown.bind(this);
        this.handleSelectorSummaryKeydown =
          this.handleSelectorSummaryKeydown.bind(this);
        this.handleSelectorToggle = this.handleSelectorToggle.bind(this);
      }

      connectedCallback() {
        if (!this.dialog || !this.drawerId) return;

        if (!this.dataset.supplementFactsMoved && this.parentElement !== document.body) {
          this.dataset.supplementFactsMoved = 'true';
          document.body.appendChild(this);
          return;
        }

        if (this.initialized) return;
        this.initialized = true;

        this.openers = document.querySelectorAll(
          `[data-supplement-facts-open="${this.drawerId}"]`,
        );

        this.openers.forEach(opener => {
          opener.addEventListener('click', this.handleOpenerClick);
        });

        this.querySelectorAll('[data-supplement-facts-close]').forEach(
          closeButton => {
            closeButton.addEventListener('click', this.handleCloseButtonClick);
          },
        );

        this.dialog.addEventListener('click', this.handleDocumentClick);
        this.dialog.addEventListener('keydown', this.handleDialogKeydown);
        this.dialog.addEventListener('close', this.handleClose);

        this.querySelectorAll('[data-supplement-facts-selector]').forEach(
          selector => {
            selector.addEventListener('toggle', this.handleSelectorToggle);
          },
        );

        this.querySelectorAll('[data-supplement-facts-selector-summary]').forEach(
          summary => {
            summary.addEventListener('keydown', this.handleSelectorSummaryKeydown);
          },
        );

        this.querySelectorAll('[data-supplement-facts-option]').forEach(
          option => {
            option.addEventListener('click', this.handleOptionClick);
            option.addEventListener('keydown', this.handleOptionKeydown);
          },
        );
      }

      disconnectedCallback() {
        this.openers?.forEach(opener => {
          opener.removeEventListener('click', this.handleOpenerClick);
        });

        this.querySelectorAll('[data-supplement-facts-close]').forEach(
          closeButton => {
            closeButton.removeEventListener('click', this.handleCloseButtonClick);
          },
        );

        this.dialog?.removeEventListener('click', this.handleDocumentClick);
        this.dialog?.removeEventListener('keydown', this.handleDialogKeydown);
        this.dialog?.removeEventListener('close', this.handleClose);

        this.querySelectorAll('[data-supplement-facts-selector]').forEach(
          selector => {
            selector.removeEventListener('toggle', this.handleSelectorToggle);
          },
        );

        this.querySelectorAll('[data-supplement-facts-selector-summary]').forEach(
          summary => {
            summary.removeEventListener('keydown', this.handleSelectorSummaryKeydown);
          },
        );

        this.querySelectorAll('[data-supplement-facts-option]').forEach(
          option => {
            option.removeEventListener('click', this.handleOptionClick);
            option.removeEventListener('keydown', this.handleOptionKeydown);
          },
        );
      }

      handleOpenerClick(event) {
        this.open(event);
      }

      handleCloseButtonClick() {
        this.close();
      }

      open(event) {
        this.openedBy = event.currentTarget;

        if (!this.dialog.open) {
          if (typeof this.dialog.showModal === 'function') {
            this.dialog.showModal();
          } else {
            this.dialog.setAttribute('open', '');
          }
        }

        this.lockBodyScroll();
        this.setBackgroundInert();

        requestAnimationFrame(() => {
          this.title?.focus();
        });
      }

      close() {
        if (this.dialog.open && typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
          this.handleClose();
        }
      }

      handleDocumentClick(event) {
        if (event.target === this.dialog) this.close();
      }

      handleDialogKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusableElements = this.getFocusableElements();
        if (!focusableElements.length) {
          event.preventDefault();
          this.title?.focus();
          return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }

      handleSelectorToggle(event) {
        this.updateSelectorExpandedState(event.currentTarget);
      }

      handleSelectorSummaryKeydown(event) {
        const selector = event.currentTarget.closest(
          '[data-supplement-facts-selector]',
        );
        if (!selector) return;

        if (
          event.key === 'Enter' ||
          event.key === ' ' ||
          event.key === 'ArrowDown' ||
          event.key === 'Down'
        ) {
          event.preventDefault();
          this.openSelector(selector);
        }

        if (event.key === 'ArrowUp' || event.key === 'Up') {
          event.preventDefault();
          this.openSelector(selector, 'last');
        }
      }

      handleOptionKeydown(event) {
        const option = event.currentTarget;
        const selector = option.closest('[data-supplement-facts-selector]');
        if (!selector) return;

        const options = this.getSelectorOptions(selector);
        const currentIndex = options.indexOf(option);

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          option.click();
          return;
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.closeSelector(selector);
          return;
        }

        if (event.key === 'Tab') {
          this.closeSelector(selector, false);
          return;
        }

        if (event.key === 'ArrowDown' || event.key === 'Down') {
          event.preventDefault();
          options[(currentIndex + 1) % options.length]?.focus();
          return;
        }

        if (event.key === 'ArrowUp' || event.key === 'Up') {
          event.preventDefault();
          options[(currentIndex - 1 + options.length) % options.length]?.focus();
          return;
        }

        if (event.key === 'Home') {
          event.preventDefault();
          options[0]?.focus();
          return;
        }

        if (event.key === 'End') {
          event.preventDefault();
          options[options.length - 1]?.focus();
          return;
        }

        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          this.focusOptionByTypeahead(event.key, options, currentIndex);
        }
      }

      handleOptionClick(event) {
        const option = event.currentTarget;
        const targetPanelId = option.dataset.targetPanel;
        const title = option.dataset.productTitle;
        const selector = option.closest('[data-supplement-facts-selector]');

        if (!targetPanelId || !selector) return;

        this.querySelectorAll('[data-supplement-facts-product-panel]').forEach(
          panel => {
            panel.hidden = panel.id !== targetPanelId;
          },
        );

        this.querySelectorAll('[data-supplement-facts-product-footer]').forEach(
          footer => {
            footer.hidden =
              footer.dataset.supplementFactsProductFooter !== targetPanelId;
          },
        );

        this.querySelectorAll('[data-supplement-facts-option]').forEach(
          item => {
            item.setAttribute(
              'aria-selected',
              item === option ? 'true' : 'false',
            );
          },
        );

        const selectedTitle = selector.querySelector(
          '[data-supplement-facts-selected-title]',
        );
        if (selectedTitle) selectedTitle.textContent = title;

        const selectedMedia = selector.querySelector(
          '[data-supplement-facts-selected-media]',
        );
        const optionMedia = option.querySelector(
          '[data-supplement-facts-option-media]',
        );
        if (selectedMedia && optionMedia) selectedMedia.innerHTML = optionMedia.innerHTML;

        this.announceSelectedProduct(selector, title);
        this.closeSelector(selector);
      }

      openSelector(selector, focusTarget = 'selected') {
        selector.setAttribute('open', '');
        this.updateSelectorExpandedState(selector);

        const options = this.getSelectorOptions(selector);
        const selectedOption = options.find(
          option => option.getAttribute('aria-selected') === 'true',
        );

        if (focusTarget === 'last') {
          options[options.length - 1]?.focus();
        } else {
          (selectedOption || options[0])?.focus();
        }
      }

      closeSelector(selector, restoreFocus = true) {
        selector.removeAttribute('open');
        this.updateSelectorExpandedState(selector);
        if (restoreFocus) {
          selector.querySelector('[data-supplement-facts-selector-summary]')?.focus();
        }
      }

      updateSelectorExpandedState(selector) {
        selector
          .querySelector('[data-supplement-facts-selector-summary]')
          ?.setAttribute('aria-expanded', selector.open ? 'true' : 'false');
      }

      getSelectorOptions(selector) {
        return Array.from(
          selector.querySelectorAll('[data-supplement-facts-option]'),
        ).filter(option => !option.disabled && !option.closest('[hidden]'));
      }

      focusOptionByTypeahead(key, options, currentIndex) {
        window.clearTimeout(this.typeaheadTimeout);
        this.typeahead += key.toLowerCase();

        const orderedOptions = [
          ...options.slice(currentIndex + 1),
          ...options.slice(0, currentIndex + 1),
        ];
        const matchingOption = orderedOptions.find(option =>
          option.dataset.productTitle?.toLowerCase().startsWith(this.typeahead),
        );

        matchingOption?.focus();

        this.typeaheadTimeout = window.setTimeout(() => {
          this.typeahead = '';
        }, 500);
      }

      announceSelectedProduct(selector, title) {
        const liveRegion = selector.parentElement?.querySelector(
          '[data-supplement-facts-live]',
        );
        if (!liveRegion) return;

        window.clearTimeout(this.liveAnnouncementTimeout);
        liveRegion.textContent = '';
        this.liveAnnouncementTimeout = window.setTimeout(() => {
          liveRegion.textContent = this.liveRegionTemplate.replace(
            '[product]',
            title,
          );
        }, 150);
      }

      getFocusableElements() {
        return Array.from(
          this.dialog.querySelectorAll(
            'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter(element => this.isFocusable(element));
      }

      isFocusable(element) {
        if (element.closest('[hidden]')) return false;
        if (element.matches('[tabindex="-1"]')) return false;
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden';
      }

      lockBodyScroll() {
        this.scrollY = window.scrollY;
        this.previousBodyStyle = {
          overflow: document.body.style.overflow,
          position: document.body.style.position,
          top: document.body.style.top,
          width: document.body.style.width,
        };

        document.body.classList.add('overflow-hidden');
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.scrollY}px`;
        document.body.style.width = '100%';
      }

      unlockBodyScroll() {
        document.body.classList.remove('overflow-hidden');
        document.body.style.overflow = this.previousBodyStyle.overflow || '';
        document.body.style.position = this.previousBodyStyle.position || '';
        document.body.style.top = this.previousBodyStyle.top || '';
        document.body.style.width = this.previousBodyStyle.width || '';
        window.scrollTo(0, this.scrollY);
      }

      setBackgroundInert() {
        this.inertedElements = [];

        Array.from(document.body.children).forEach(element => {
          if (element === this || element.matches('script, style')) return;

          this.inertedElements.push({
            element,
            ariaHidden: element.getAttribute('aria-hidden'),
            inert: element.inert,
          });

          element.inert = true;
          element.setAttribute('aria-hidden', 'true');
        });
      }

      restoreBackground() {
        this.inertedElements.forEach(({element, ariaHidden, inert}) => {
          element.inert = inert;
          if (ariaHidden === null) {
            element.removeAttribute('aria-hidden');
          } else {
            element.setAttribute('aria-hidden', ariaHidden);
          }
        });

        this.inertedElements = [];
      }

      handleClose() {
        this.querySelectorAll('[data-supplement-facts-selector]').forEach(
          selector => this.closeSelector(selector, false),
        );
        this.unlockBodyScroll();
        this.restoreBackground();

        window.setTimeout(() => {
          if (this.openedBy && document.contains(this.openedBy)) {
            this.openedBy.focus();
          } else {
            document.querySelector('main')?.focus();
          }
        });
      }
    },
  );
}
